import React, { useState, useEffect, useCallback, useRef } from 'react';
import { FaceLivenessDetector } from '@aws-amplify/ui-react-liveness';
import '@aws-amplify/ui-react-liveness/styles.css';
import { useStore } from '../Context';
import faceliveness from '../services/faceliveness';
import signs from '../services/signs';
import { facelivenessText } from '../utils/facelivenessText';
import { useTimer } from '../hooks/useTimer';
import { FACELIVENESS_SESSION_TIMEOUT, FULLY_VISIBLE_THRESHOLD, Label, StickyButton } from '../constants/faceliveness';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import { modalTypes } from '../components/Modal/SignatureModal';

const StepFaceLiveness = () => {
    const {
        stepForward,
        setError,
        setLoading,
        discountAttemp,
        setAttempts,
        faceliveness_validation,
        openModal,
        attempts,
        isModalOpen,
        requestId,
        dni,
        gender,
        hash,
        setToken,
        setFaceLivenessImageUrl,
        photoUri,
    } = useStore();
    const [sessionId, setSessionId] = useState(null);
    const [isSessionIsCreated, setIsSessionIsCreated] = useState(false);
    const { isExpired, startTimer, resetTimer, timeLeftFormatted, stopTimer } = useTimer(FACELIVENESS_SESSION_TIMEOUT);
    const [isSessionError, setIsSessionError] = useState(false);
    const [isButtonScrollVisible, setIsButtonScrollVisible] = useState(true);
    const endRef = useRef(null);

    const createSession = useCallback(async () => {
        if (!faceliveness_validation) return stepForward();
        try {
            const response = await faceliveness.createSession();
            setSessionId(response.sessionId);
            setIsSessionIsCreated(true);
            setIsSessionError(false);
            if (isExpired) {
                resetTimer();
            }
            startTimer();
        } catch (err) {
            setIsSessionError(true);
            openModal({ type: modalTypes.facelivenessCreateSessionError.type });
            console.error('Error at createSession:', err);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [faceliveness_validation, setError, stepForward]);

    const handleError = async () => {
        discountAttemp();
        if (requestId) {
            faceliveness.updateAttempts(requestId).catch(err =>
                console.error('[FaceLiveness] updateAttempts error:', err)
            );
        }
        if (attempts > 1) {
            openModal({ type: modalTypes.faceValidationRetry.type });
        } else {
            setError({
                "error": "Cuotitas Request is cancelled.",
                "extra": {
                    "key": "attempts_are_over"
                }
            });
        }
    };

    const handleBackendValidationFailure = (data) => {
        if (data.attempts && typeof data.attempts.attempts === 'number') {
            setAttempts(data.attempts.attempts);
        }
        const rem = data.attempts?.attempts;
        if (rem !== undefined && rem !== null) {
            if (rem > 0) {
                openModal({ type: modalTypes.faceValidationRetry.type });
            } else {
                setError({
                    "error": "Cuotitas Request is cancelled.",
                    "extra": {
                        "key": "attempts_are_over"
                    }
                });
            }
            return;
        }
        if (attempts > 1) {
            openModal({ type: modalTypes.faceValidationRetry.type });
        } else {
            setError({
                "error": "Cuotitas Request is cancelled.",
                "extra": {
                    "key": "attempts_are_over"
                }
            });
        }
    };

    const handleAnalysisComplete = async () => {
        try {
            if (!sessionId) {
                throw new Error('Session ID is null');
            }
            if (!requestId) {
                throw new Error('requestId is null');
            }
            setLoading(true);
            const data = await faceliveness.postSessionResults(sessionId, {
                requestId,
                document: dni,
                gender
            });

            if (data.passed) {
                if (!data.referenceImagePresignedUrl) {
                    throw new Error('Missing referenceImagePresignedUrl after Face Liveness validation');
                }
                await signs.postFaceLivenessLogin(hash, requestId, data.referenceImagePresignedUrl, setToken);
                await signs.postDocument(photoUri, requestId);
                setFaceLivenessImageUrl(data.referenceImagePresignedUrl);
                stepForward();
            } else {
                handleBackendValidationFailure(data);
            }
        } catch (err) {
            await handleError();
        } finally {
            resetTimer();
            setLoading(false);
        }
    };

    const handleUserCancel = () => {
        resetTimer();
        setSessionId(null);
        openModal({ type: modalTypes.onCancelFaceliveness.type });
        createSession();
    };

    useEffect(() => {
        let isMounted = true;
        try {
            if (!sessionId) {
                if (isMounted) {
                    createSession();
                }
            }
        } catch (err) {
            isMounted = false;
        }
        return () => { isMounted = false; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (sessionId) {
            const observer = new IntersectionObserver(
                ([entry]) => {
                    if (entry.isIntersecting) {
                        setIsButtonScrollVisible(false);
                    } else {
                        setIsButtonScrollVisible(true);
                    }
                },
                { threshold: FULLY_VISIBLE_THRESHOLD }
            );

            observer.observe(endRef.current);

            return () => observer.disconnect();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [sessionId]);

    useEffect(() => {
        if (!isModalOpen && isExpired && isSessionIsCreated) {
            setIsSessionIsCreated(false);
            stopTimer();
            handleError();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isModalOpen, isExpired]);

    useEffect(() => {
        if (!isModalOpen && isExpired && !isSessionIsCreated) {
            resetTimer();
            createSession();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isModalOpen, isExpired, isSessionIsCreated]);

    useEffect(() => {
        if (isSessionError && !isModalOpen) {
            createSession();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isSessionError, isModalOpen]);

    return (
        <div className="liveness-container">
            <Label>Te quedan {attempts} intentos</Label>
            <Label>El intento expira en {timeLeftFormatted}</Label>
            {sessionId && (
                <FaceLivenessDetector
                    sessionId={sessionId}
                    region="us-east-1"
                    onAnalysisComplete={handleAnalysisComplete}
                    onError={handleError}
                    onUserCancel={handleUserCancel}
                    displayText={facelivenessText}
                    components={{
                        ErrorView: () => null,
                    }}
                />
            )}
            <div ref={endRef} id="button-faceliveness"></div>
            {isButtonScrollVisible && !isModalOpen && (
                <StickyButton href="#button-faceliveness">
                    <ArrowDownwardIcon />
                </StickyButton>
            )}
        </div>
    );
};

export default StepFaceLiveness;
